import React, { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import axios from 'axios';
import {
  Box,
  Drawer,
  CircularProgress,
  List,
  ListItem,
  FormControlLabel,
  Switch,
  IconButton
} from '@material-ui/core';
import { Share as ShareIcon, DeleteSweep as DeleteIcon, Edit as EditIcon } from '@material-ui/icons';
import AppointContext from '@this/components/appoint/appoint_context';
import { DrawerIn, DrawerTitle, DrawerBody, CloseButton } from '@this/components/appoint/common';
import muiTheme from '@this/components/appoint/mui_theme';
import NewBigButton from '@this/components/appoint/shared/new_big_button/new_big_button';
import type { OpenMeetingStatus, OpenMeetingResponseArgs } from '@this/domain/appoint/open_meeting';
import OpenMeeting, { convertOpenMeetingResponseToArgs } from '@this/domain/appoint/open_meeting';
import { useHistory } from 'react-router-dom';
import OpenMeetingDeleteModal from './open_meeting_delete_modal';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface Response {
  open_meetings: OpenMeetingResponseArgs[];
}

const OpenMeetingDrawer = observer(({ open, onClose }: Props) => {
  const contextValue = useContext(AppointContext);
  const history = useHistory();
  const [openMeetings, setOpenMeetings] = useState<OpenMeeting[]>([]);
  const [deleteTargetMeeting, setDeleteTargetMeeting] = useState<OpenMeeting | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchOpenMeetings = () => {
    setIsLoading(true);
    axios
      .get<Response>('/aicalendar/open_meetings.json')
      .then(res => {
        setOpenMeetings(res.data.open_meetings.map(raw => new OpenMeeting(convertOpenMeetingResponseToArgs(raw))));
      })
      .finally(() => {
        setIsLoading(false);
      })
      .catch(() => {
        // TODO
      });
  };

  useEffect(() => {
    fetchOpenMeetings();
  }, []);

  const togglePublishedStatus = (openMeeting: OpenMeeting, status: OpenMeetingStatus) => {
    if (!openMeeting.id) {
      return;
    }

    const params = openMeeting.submitParams();
    params.status = status;

    axios
      .put(`/aicalendar/open_meetings/${openMeeting.id}.json`, params)
      .then(() => {
        openMeeting.status = status;
        contextValue.setSnackbarContent({
          message: `「${openMeeting.workingTitle}」を${
            openMeeting.published ? '公開しました' : '非公開にしました'
          }`,
          severity: 'success'
        });
        contextValue.setOpenSnackbar(true);
      })
      .catch(() => {
        contextValue.setSnackbarContent({
          message: `「${openMeeting.workingTitle}」のステータス変更に失敗しました`,
          severity: 'error'
        });
        contextValue.setOpenSnackbar(true);
      });
  };

  const handleClickEditButton = (openMeeting: OpenMeeting) => {
    const path = `/open_meetings/${openMeeting.key}/edit`;
    history.push(path);
  };

  const handleClickShareButton = (openMeeting: OpenMeeting) => {
    const path = history.createHref({
      pathname: `/open_meetings/${openMeeting.key}`
    });
    const url = `${location.origin}${path}`;
    navigator.clipboard.writeText(url);
    contextValue.setSnackbarContent({
      message: '公開URLをクリップボードにコピーしました',
      severity: 'success'
    });
    contextValue.setOpenSnackbar(true);
  };

  const handleClickDeleteButton = (openMeeting: OpenMeeting) => {
    setDeleteTargetMeeting(openMeeting);
  };

  const handleDeleteOpenMeeting = (openMeeting: OpenMeeting) => {
    axios
      .delete(`/aicalendar/open_meetings/${openMeeting.key}.json`)
      .then(() => {
        setDeleteTargetMeeting(null);
        fetchOpenMeetings();
      })
      .catch(() => {
        contextValue.setSnackbarContent({
          message: `「${openMeeting.workingTitle}」の削除に失敗しました`,
          severity: 'error'
        });
        contextValue.setOpenSnackbar(true);
      });
  };

  return (
    <Drawer
      open={open}
      anchor="left"
      variant="persistent"
      onClick={e => e.stopPropagation()}
      onClose={() => onClose()}
      PaperProps={{
        style: {
          position: contextValue.isMobile ? 'fixed' : 'absolute',
          zIndex: 100,
          overflow: 'hidden',
          boxSizing: 'border-box',
          minWidth: contextValue.isMobile ? '100vw' : '100%',
          borderTop: 'solid 10px #243565',
          borderRight: '0'
        }
      }}
    >
      <DrawerIn>
        <DrawerTitle paddingLeft="0">
          <Box>公開URL</Box>
          <CloseButton onClick={() => onClose()} />
        </DrawerTitle>
        <DrawerBody>
          <Box margin="0 20px">
            <NewBigButton to="/open_meetings/new" label="公開URLを作成" />
          </Box>
          {isLoading && (
            <Box marginTop="20px" textAlign="center">
              <CircularProgress />
            </Box>
          )}
          <List style={{ marginTop: '20px', padding: '0 20px' }}>
            {openMeetings.map(om => (
              <ListItem
                key={om.id}
                style={{
                  border: `1px solid ${muiTheme.palette.grey[200]}`,
                  marginBottom: '10px',
                  borderRadius: '3px',
                  padding: '10px',
                  display: 'block'
                }}
              >
                <Box fontWeight="bold" fontSize="12px">
                  {om.workingTitle}
                </Box>
                <Box marginTop="5px" fontSize="10px">
                  {om.duration}分
                </Box>
                <Box display="flex" alignItems="center">
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={om.published}
                        onChange={e => {
                          const status = e.target.checked ? 'published' : 'hidden';
                          togglePublishedStatus(om, status);
                        }}
                      />
                    }
                    label={<span style={{ fontSize: 10 }}>公開</span>}
                    labelPlacement="start"
                    style={{
                      color: om.published ? muiTheme.palette.primary.main : muiTheme.palette.grey[700],
                      marginLeft: 0
                    }}
                  />
                  <IconButton
                    size="small"
                    style={{ marginLeft: 'auto' }}
                    onClick={() => handleClickEditButton(om)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    style={{ marginLeft: '0.2em' }}
                    onClick={() => handleClickShareButton(om)}
                  >
                    <ShareIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    style={{ marginLeft: '0.2em' }}
                    onClick={() => handleClickDeleteButton(om)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>
        </DrawerBody>
      </DrawerIn>
      {deleteTargetMeeting && (
        <OpenMeetingDeleteModal
          deleteTargetMeeting={deleteTargetMeeting}
          onClose={() => setDeleteTargetMeeting(null)}
          onClickDelete={() => handleDeleteOpenMeeting(deleteTargetMeeting)}
        />
      )}
    </Drawer>
  );
});

export default OpenMeetingDrawer;
